<template>
  <div class="size-guide-btn">
    <btn
      v-if="hasSizeGuide"
      class="size-guide-btn__button"
      modifiers="text"
      type="button"
      :label="$string('product.form.size_guide')"
      :aria-label="`Open ${$string('product.form.size_guide')}`"
      @click.prevent="handleDrawerToggle"
    />

    <!-- Size guide drawer -->
    <teleport
      v-if="hasSizeGuide"
      :disabled="!teleport"
      to="body"
    >
      <overlay
        block-class="size-guide-overlay"
        direction="left"
        :namespace="namespace"
        type="drawer"
        :teleport="teleport"
      >
        <template #title>
          <h3
            :id="namespace"
            class="text-body-l-regular"
            v-text="title"
          />
        </template>
        <template #body="overlay">
          <size-guide
            v-if="getComponentVisible('size-guide')"
            v-cloak
            :overlay="overlay"
            :product="product"
            :data="sizeGuide"
            @update-guide="guide => activeGuide = guide"
          />
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Size guide btn
 * -----------------------------------------------------------------------------
 * Size guide button
 *
 * @param {object} product - Canvas formatted product
 * @param {Boolean} [teleport] - Teleport size guide overlay and window to
 * end of <body>.
 */

import { defineAsyncComponent } from 'vue'
import { mapActions } from 'vuex'

import Overlay from '~global/overlay/overlay'
import Btn from '~global/btn/btn'

import { track } from '~/helpers/datalayer-actions'
import { formatSizeGuide } from '~/helpers/format'
import { getVariables } from '~/helpers/graphql'
import { convertToHandle } from '~/helpers/convert'
import { generateUUID } from '~/helpers/general'

import sizeGuideQuery from '~graphql/sizeGuideMetaobject.gql'

export default {
  name: 'SizeGuideBtn',

  components: {
    Btn,
    Overlay,
    SizeGuide: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.size-guide' */
        '~async/size-guide/size-guide'
      ),
    }),
  },

  inject: {
    context: {
      default: 'PDP',
    },
  },

  props: {
    product: {
      type: Object,
      default: () => null,
      required: true,
    },
    teleport: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      namespace: this.context === 'PDP'
        ? 'sizeGuide-main'
        : `sizeGuide-${generateUUID()}`,
      sizeGuide: null,
      activeGuide: {},
    }
  },

  computed: {

    /**
     * Compute if size guide exists in:
     * - Theme variables
     * - Cached session storage
     * @returns {boolean}
     */
    hasSizeGuide() {
      return Boolean(this.sizeGuide)
    },

    /**
     * Compute drawer title
     * @returns {string}
     */
    title() {
      return this.activeGuide?.titleOverride ||
        this.sizeGuide?.title ||
        this.$string('size_guide.default_title') ||
        ''
    },
  },

  watch: {
    product(newProduct, oldProduct) {
      if (newProduct?.handle === oldProduct?.handle) {
        return
      }
      this.getSizeGuide()
    },
  },

  created() {
    this.getSizeGuide()
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
    }),

    /**
     * Get size guide metaobject using GraphQL query
     */
    async getSizeGuide() {
      if (!this.product) {
        return
      }

      try {
        const response = await cnvs.Query({
          query: sizeGuideQuery,
          variables: getVariables({
            handle: {
              type: 'size_guide_collection',
              handle: convertToHandle(this.product.type),
            },
          }),
        })

        this.sizeGuide = formatSizeGuide(response)
      } catch (error) {
        cnvs.ReportError('[Size Guide Query]', error)
      }
    },

    /**
     * Handle menu toggle.
     */
    handleDrawerToggle() {
      /**
       * Size guide view
       */
      let event = 'size_guide_view'
      if (this.context === 'drawer') {
        event = `quick_add_${event}`
      } else if (this.context !== 'PDP') {
        event = `${this.context}_${event}`
      }

      track({
        event,
        productType: this.product.type,
        style: this.product.styleNumber,
      })

      this.openOverlay({
        component: 'size-guide',
        ignoreDismissed: true,
        namespace: this.namespace,
        queue: false,
      })
    },
  },
}
</script>

<style lang="scss">
@import './size-guide-btn';
</style>
